import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import {
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import GhashTab from './admin/GhashTab';

const AdminUsers = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('user-management');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approve`);
      toast.success('User approved successfully.');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve user');
    }
  };

  // handleStatusChange is reserved for future use
  // const handleStatusChange = async (userId, status) => {
  //   try {
  //     await api.put(`/admin/users/${userId}/status`, { status });
  //     toast.success(`User status updated to ${status}`);
  //     fetchUsers();
  //   } catch (error) {
  //     toast.error(error.response?.data?.error || 'Failed to update status');
  //   }
  // };

  const handleViewDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/overview`);
      if (response.data.success && response.data.overview) {
        setSelectedUser(response.data.overview);
        setShowUserDetail(true);
      } else {
        toast.error('Failed to fetch user details');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to fetch user details');
    }
  };

  const handleLoginAsUser = async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/login-token`);
      if (response.data.success && response.data.token) {
        // Open new tab with login token
        const loginUrl = `${window.location.origin}/login?token=${response.data.token}`;
        window.open(loginUrl, '_blank');
        toast.success(`Opening ${response.data.user.username}'s account in new tab...`);
      } else {
        toast.error('Failed to generate login token');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to login as user');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    const label = targetUser.username || targetUser.email || 'this user';
    const statusNote = targetUser.status === 'pending_approval'
      ? ' This pending signup will be removed permanently.'
      : '';
    if (!window.confirm(`Delete @${label} from the database?${statusNote} This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${targetUser._id}`);
      toast.success(`User @${label} deleted`);
      if (selectedUser?.user?._id === targetUser._id) {
        setShowUserDetail(false);
        setSelectedUser(null);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'pending_approval': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'frozen': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'suspended': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const tabs = [
    { id: 'user-management', label: 'User Management' },
    { id: 'user-analytics', label: 'User Analytics' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'packages', label: 'Packages' },
    { id: 'ghash', label: 'Ghash' },
    { id: 'infrastructure', label: 'Infrastructure' },
    { id: 'settings', label: 'Settings' }
  ];

  if (loading && activeTab === 'user-management') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-br from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute -top-16 left-1/3 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -bottom-20 -right-16 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute top-1/2 -left-10 w-48 h-48 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes - admin/shield themed */}
          <svg className="absolute top-8 right-12 w-20 h-20 opacity-10" viewBox="0 0 100 100">
            <path d="M50,10 L85,30 L85,60 Q85,85 50,95 Q15,85 15,60 L15,30 Z" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
          </svg>
          <svg className="absolute bottom-8 left-1/4 w-16 h-16 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="35" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
            <circle cx="50" cy="40" r="12" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
            <path d="M30,75 Q30,55 50,55 Q70,55 70,75" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
          </svg>
          <svg className="absolute top-1/3 left-10 w-12 h-12 opacity-10" viewBox="0 0 100 100">
            <polygon points="50,15 61,40 88,40 67,58 76,85 50,68 24,85 33,58 12,40 39,40" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <ShieldCheckIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Admin Management
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage users, packages, and system settings
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                    }`} />
                    {users.filter(u => u.status === 'active').length} Active Users
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-yellow-400' : 'bg-yellow-500'
                    }`} />
                    {users.filter(u => u.status === 'pending_approval').length} Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-600 dark:text-primary-400 dark:border-primary-400'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'user-management' && (
        <UserManagementTab
          users={users}
          loading={loading}
          currentUserId={user?._id}
          getStatusBadge={getStatusBadge}
          handleApprove={handleApprove}
          handleLoginAsUser={handleLoginAsUser}
          handleViewDetails={handleViewDetails}
          handleDeleteUser={handleDeleteUser}
        />
      )}

      {activeTab === 'user-analytics' && (
        <UserAnalyticsTab />
      )}

      {activeTab === 'revenue' && (
        <RevenueTab />
      )}

      {activeTab === 'packages' && (
        <PackagesTab />
      )}

      {activeTab === 'ghash' && (
        <GhashTab />
      )}

      {activeTab === 'infrastructure' && <InfrastructureTab />}

      {activeTab === 'settings' && (
        <SettingsTab />
      )}

      {showUserDetail && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => {
            setShowUserDetail(false);
            setSelectedUser(null);
          }}
          onUpdate={async () => {
            await fetchUsers();
            // Refresh selected user overview data
            try {
              const userId = selectedUser.user?._id || selectedUser._id;
              if (userId) {
                const response = await api.get(`/admin/users/${userId}/overview`);
                if (response.data.success && response.data.overview) {
                  setSelectedUser(response.data.overview);
                }
              }
            } catch (error) {
              console.error('Failed to refresh user overview:', error);
            }
          }}
        />
      )}
    </div>
  );
};

// User Management Tab Component
const UserManagementTab = ({
  users,
  loading,
  currentUserId,
  getStatusBadge,
  handleApprove,
  handleLoginAsUser,
  handleViewDetails,
  handleDeleteUser
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Contact
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Package
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Features
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Accounts
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px]">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {users.map((user) => (
            <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.username}
                  </div>
                  {user.isBetaTester && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                      BETA
                    </span>
                  )}
                  {user.isTrialUser && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                      TRIAL
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {user.name}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 dark:text-white">
                  <a href={`mailto:${user.email}`} className="text-primary-600 hover:text-primary-800">
                    {user.email}
                  </a>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <a href={`https://t.me/${user.telegram}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800">
                    @{user.telegram}
                  </a>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(user.status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.packageTier ? (
                  <div>
                    <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                      {user.packageTier}
                    </span>
                    {user.packageStatus && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {user.packageStatus === 'active' ? 'Active' : user.packageStatus}
                        {user.subscriptionEndDate && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            Exp: {new Date(user.subscriptionEndDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500">No package</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-wrap gap-1">
                  {user.features?.canAddAccounts && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                      Accounts
                    </span>
                  )}
                  {user.features?.canUseAutomation && (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                      Automation
                    </span>
                  )}
                  {user.features?.canUseScraper && (
                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                      Scraper
                    </span>
                  )}
                  {user.features?.canUseMassDM && (
                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded">
                      Mass DM
                    </span>
                  )}
                  {user.features?.canUseMassLike && (
                    <span className="px-2 py-1 text-xs bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200 rounded">
                      Mass Like
                    </span>
                  )}
                  {user.features?.canUseMassTasks && (
                    <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded">
                      Mass Tasks
                    </span>
                  )}
                  {user.features?.canUseFUFMethod && (
                    <span className="px-2 py-1 text-xs bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 rounded">
                      F/UF Method
                    </span>
                  )}
                  {(!user.features || Object.values(user.features).every(v => !v)) && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">
                      No features
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {user.accountsCount || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium min-w-[120px]">
                <div className="flex space-x-2">
                  {user.status === 'pending_approval' && (
                    <button
                      onClick={() => handleApprove(user._id)}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 whitespace-nowrap"
                    >
                      Approve
                    </button>
                  )}
                  {user.status === 'active' && (
                    <button
                      onClick={() => handleLoginAsUser(user._id)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap"
                      title="Login as this user"
                    >
                      Login
                    </button>
                  )}
                  <button
                    onClick={() => handleViewDetails(user._id)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 whitespace-nowrap"
                  >
                    View
                  </button>
                  {String(user._id) !== String(currentUserId) && (
                    <button
                      type="button"
                      onClick={() => handleDeleteUser(user)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 whitespace-nowrap"
                      title="Delete user permanently"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// User Analytics Tab Component
const UserAnalyticsTab = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users/analytics');
      if (response.data.success) {
        setAnalytics(response.data.analytics || []);
      }
    } catch (error) {
      toast.error('Failed to fetch user analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getStatusBadge = (status) => {
    const colors = {
      'pending_approval': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'frozen': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'suspended': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4 flex items-center justify-end">
        <button
          onClick={fetchAnalytics}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {/* Analytics Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Package</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Accounts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Conversations</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Models</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Automations</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Responses</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Performance</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {analytics.map((item) => (
                <tr key={item.user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{item.user.username}</div>
                      {item.user.isBetaTester && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                          BETA
                        </span>
                      )}
                      {item.user.isTrialUser && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                          TRIAL
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.user.email}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getStatusBadge(item.user.status)}
                  </td>
                  <td className="px-4 py-4">
                    {item.conversations.subscription?.packageTier ? (
                      <div>
                        <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                          {item.conversations.subscription.packageTier}
                        </span>
                        {item.conversations.subscription.endDate && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Exp: {new Date(item.conversations.subscription.endDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">No package</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {item.accounts.total} / {item.accounts.limit === 0 ? '∞' : item.accounts.limit}
                    </div>
                    {item.accounts.limit > 0 && (
                      <div className="mt-1">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              item.accounts.percentage >= 90 ? 'bg-red-500' :
                              item.accounts.percentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(item.accounts.percentage, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {item.accounts.active} active
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 dark:text-white font-semibold">
                      {item.user.role === 'admin' 
                        ? `${item.conversations.remaining?.toLocaleString() || 0} Conversations`
                        : `${item.conversations.remaining?.toLocaleString() || 0} remaining`
                      }
                    </div>
                    {item.user.role !== 'admin' && item.conversations.monthlyAllowance > 0 && (
                      <div className="mt-1">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Allowance: {item.conversations.monthlyAllowance?.toLocaleString() || 0}
                          {item.conversations.extraBalance > 0 && ` • Extras: ${item.conversations.extraBalance.toLocaleString()}`}
                        </div>
                        {item.conversations.subscription?.packageTier && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Package: {item.conversations.subscription.packageTier}
                            {item.conversations.subscription.endDate && (
                              <span className="ml-1">
                                (expires {new Date(item.conversations.subscription.endDate).toLocaleDateString()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {item.user.role === 'admin' && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Admin (unlimited)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                    {item.models.total}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {item.automations.active > 0 ? (
                        <span className="text-green-600 dark:text-green-400 font-semibold">
                          ● {item.automations.active} Active
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 dark:text-white font-semibold">
                      {item.responses.total.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {item.responses.ctas} CTAs • {item.responses.conversions} Conversions
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <div className="text-gray-900 dark:text-white font-semibold">
                        {item.responses.conversionRate}% CR
                      </div>
                      {item.lastActivity && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(item.lastActivity).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const UserDetailModal = ({ user: overviewData, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Handle both old format (user object) and new format (overview object)
  const overview = useMemo(() => {
    return overviewData?.user ? overviewData : { user: overviewData };
  }, [overviewData]);
  
  const [features, setFeatures] = useState({
    canAddAccounts: overview?.user?.features?.canAddAccounts || false,
    canUseAutomation: overview?.user?.features?.canUseAutomation || false,
    canUseScraper: overview?.user?.features?.canUseScraper || false,
    canUseMassDM: overview?.user?.features?.canUseMassDM || false,
    canUseMassLike: overview?.user?.features?.canUseMassLike || false,
    canUseMassTasks: overview?.user?.features?.canUseMassTasks || false,
    canUseFUFMethod: overview?.user?.features?.canUseFUFMethod || false
  });
  const [limits, setLimits] = useState({
    maxAccounts: overview?.user?.limits?.maxAccounts || 0,
    maxConversations: overview?.user?.limits?.maxConversations || 0
  });
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isBetaTester, setIsBetaTester] = useState(overview?.user?.isBetaTester || false);
  const [isTrialUser, setIsTrialUser] = useState(overview?.user?.isTrialUser || false);
  
  const user = overview?.user || {};
  const accounts = overview?.accounts || {};
  const conversations = overview?.conversations || {};
  const models = overview?.models || {};
  const automations = overview?.automations || {};
  const responses = overview?.responses || {};
  
  // Update state when user prop changes
  useEffect(() => {
    if (overview?.user) {
      setFeatures({
        canAddAccounts: overview.user.features?.canAddAccounts || false,
        canUseAutomation: overview.user.features?.canUseAutomation || false,
        canUseScraper: overview.user.features?.canUseScraper || false,
        canUseMassDM: overview.user.features?.canUseMassDM || false,
        canUseMassLike: overview.user.features?.canUseMassLike || false,
        canUseMassTasks: overview.user.features?.canUseMassTasks || false,
        canUseFUFMethod: overview.user.features?.canUseFUFMethod || false
      });
      setLimits({
        maxAccounts: overview.user.limits?.maxAccounts || 0,
        maxConversations: overview.user.limits?.maxConversations || 0
      });
      setIsBetaTester(overview.user.isBetaTester || false);
      setIsTrialUser(overview.user.isTrialUser || false);
    }
  }, [overview]);
  
  const refreshOverview = async () => {
    if (!user._id) return;
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${user._id}/overview`);
      if (response.data.success && response.data.overview) {
        // Update parent component's selectedUser via onUpdate callback
        await onUpdate();
      }
    } catch (error) {
      console.error('Failed to refresh overview:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFeatures = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/features`, { features });
      toast.success('Features updated successfully');
      await refreshOverview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update features');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateLimits = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/limits`, limits);
      toast.success('Limits updated successfully');
      await refreshOverview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update limits');
    } finally {
      setUpdating(false);
    }
  };

  const handleTopUpConversations = async (amount, addToExtras) => {
    setUpdating(true);
    try {
      await api.post(`/admin/users/${user._id}/topup-conversations`, {
        amount: parseInt(amount),
        addToExtras: addToExtras || false
      });
      toast.success(`Added ${amount} conversations successfully`);
      await refreshOverview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to top up conversations');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleBetaTester = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/beta-tester`, {
        isBetaTester: !isBetaTester
      });
      setIsBetaTester(!isBetaTester);
      // If enabling beta tester, disable trial user (mutually exclusive)
      if (!isBetaTester) {
        setIsTrialUser(false);
      }
      toast.success(isBetaTester ? 'Beta-tester status removed' : 'User set as beta-tester');
      await refreshOverview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update beta-tester status');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleTrialUser = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/trial`, {
        isTrialUser: !isTrialUser
      });
      setIsTrialUser(!isTrialUser);
      // If enabling trial user, disable beta tester (mutually exclusive)
      if (!isTrialUser) {
        setIsBetaTester(false);
      }
      toast.success(isTrialUser ? 'Trial user status removed' : 'User set as trial user');
      await refreshOverview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update trial user status');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (newPassword) => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/password`, {
        newPassword: newPassword
      });
      toast.success(`Password changed successfully for ${user.username}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setUpdating(false);
    }
  };


  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'models', label: 'Models' },
    { id: 'actions', label: 'Actions' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {user.username}
                </h2>
                {user.isBetaTester && (
                  <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                    BETA TESTER
                  </span>
                )}
                {user.isTrialUser && (
                  <span className="px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                    TRIAL USER
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {user.email} • @{user.telegram}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl"
            >
              ✕
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600 dark:text-primary-400 dark:border-primary-400'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-600 dark:text-gray-400">Loading...</div>
            </div>
          )}
          
          {!loading && activeTab === 'overview' && (
            <OverviewTab 
              user={user}
              accounts={accounts}
              conversations={conversations}
              models={models}
              automations={automations}
              responses={responses}
              lastActivity={overview?.lastActivity}
            />
          )}
          
          {!loading && activeTab === 'models' && (
            <ModelsTab models={models} />
          )}
          
          {!loading && activeTab === 'actions' && (
            <ActionsTab 
              user={user}
              features={features}
              setFeatures={setFeatures}
              limits={limits}
              setLimits={setLimits}
              conversations={conversations}
              isBetaTester={isBetaTester}
              isTrialUser={isTrialUser}
              onUpdateFeatures={handleUpdateFeatures}
              onUpdateLimits={handleUpdateLimits}
              onTopUpConversations={handleTopUpConversations}
              onToggleBetaTester={handleToggleBetaTester}
              onToggleTrialUser={handleToggleTrialUser}
              onChangePassword={handleChangePassword}
              updating={updating}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ user, accounts, conversations, models, automations, responses, lastActivity }) => {
  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Accounts"
          value={accounts.total || 0}
          limit={accounts.limit || 0}
          remaining={accounts.remaining}
          active={accounts.active || 0}
        />
        <MetricCard
          title="Conversations"
          value={conversations.remaining || 0}
          limit={conversations.monthlyAllowance || 0}
          remaining={conversations.remaining}
          extraBalance={conversations.extraBalance}
          subscription={conversations.subscription}
        />
        <MetricCard
          title="Models"
          value={models.total || 0}
        />
        <MetricCard
          title="Active Automations"
          value={automations.active || 0}
        />
      </div>
      
      {/* Responses Section */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Responses & Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Responses</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{responses.total || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">CTAs Sent</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{responses.ctas || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Conversions</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{responses.conversions || 0}</p>
          </div>
        </div>
      </div>
      
      {/* Account Limits */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Limits</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Max Accounts:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {accounts.limit === 0 ? 'Unlimited' : `${accounts.limit}`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Conversations Remaining:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {conversations.remaining?.toLocaleString() || 0}
            </span>
          </div>
          {conversations.monthlyAllowance > 0 && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Monthly Allowance:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {conversations.monthlyAllowance.toLocaleString()}
                </span>
              </div>
              {conversations.extraBalance > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Extra Balance:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {conversations.extraBalance.toLocaleString()}
                  </span>
                </div>
              )}
              {conversations.subscription?.packageTier && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Package:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {conversations.subscription.packageTier}
                    {conversations.subscription.endDate && (
                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
                        (expires {new Date(conversations.subscription.endDate).toLocaleDateString()})
                      </span>
                    )}
                  </span>
                </div>
              )}
            </>
          )}
          {lastActivity && (
            <div className="flex justify-between mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Last Activity:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {new Date(lastActivity).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, limit, remaining, active, extraBalance, subscription }) => {
  // For conversations, calculate percentage based on remaining vs allowance
  const percentage = limit > 0 && title === 'Conversations' 
    ? Math.round(((limit - remaining + (extraBalance || 0)) / limit) * 100)
    : limit > 0 ? Math.round((value / limit) * 100) : null;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
      {limit > 0 && (
        <div className="mt-2">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Limit: {limit}</span>
            {remaining !== null && <span>Remaining: {remaining}</span>}
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${percentage >= 90 ? 'bg-red-500' : percentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>
      )}
      {active !== undefined && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          {active} active
        </p>
      )}
    </div>
  );
};


// Models Tab Component
const ModelsTab = ({ models }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        Models ({models.total || 0})
      </h3>
      
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Age</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Gender</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {models.list && models.list.length > 0 ? (
              models.list.map((model) => (
                <tr key={model.modelId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    <div>
                      <div>{model.displayName || model.name}</div>
                      {model.displayName && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Model: {model.name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{model.age}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">{model.gender}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">{model.platformType}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {new Date(model.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No models found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Actions Tab Component
const ActionsTab = ({ user, features, setFeatures, limits, setLimits, conversations, isBetaTester, isTrialUser, onUpdateFeatures, onUpdateLimits, onTopUpConversations, onToggleBetaTester, onToggleTrialUser, onChangePassword, updating }) => {
  const [topUpAmount, setTopUpAmount] = useState('');
  const [addToExtras, setAddToExtras] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userRole, setUserRole] = useState(user.role || 'user');
  const [roleUpdating, setRoleUpdating] = useState(false);

  const handleUpdateRole = async () => {
    setRoleUpdating(true);
    try {
      await api.put(`/admin/users/${user._id}/role`, { role: userRole });
      toast.success('Role updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role');
    } finally {
      setRoleUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">User Role</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-sm text-gray-600 dark:text-gray-400">Role</label>
            <select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              className="mt-1 block rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            >
              <option value="user">User</option>
              <option value="va">Virtual Assistant</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleUpdateRole}
            disabled={roleUpdating}
            className="btn-primary"
          >
            {roleUpdating ? 'Saving...' : 'Update role'}
          </button>
        </div>
      </div>

      {/* Trial User Status */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Trial User Status</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Trial User Status
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isTrialUser 
                  ? 'User has trial access (10 accounts, 200 conversations)'
                  : 'User does not have trial access'}
              </p>
            </div>
            <button
              onClick={onToggleTrialUser}
              disabled={updating}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                isTrialUser
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              } disabled:opacity-50`}
            >
              {updating 
                ? 'Updating...' 
                : isTrialUser 
                  ? 'Remove Trial User' 
                  : 'Set as Trial User'}
            </button>
          </div>
          {isTrialUser && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded p-3">
              <p className="text-sm text-purple-800 dark:text-purple-200">
                <strong>Trial User Benefits:</strong>
              </p>
              <ul className="text-xs text-purple-700 dark:text-purple-300 mt-1 list-disc list-inside">
                <li>10 accounts limit (unless admin sets different)</li>
                <li>200 conversations (unless admin sets different)</li>
                <li>Basic features enabled: Add Accounts, Automation</li>
                <li>Other features (Scraper, Mass DM) must be enabled manually by admin</li>
                <li>Account activated automatically</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Beta-Tester Status */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Beta-Tester Status</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Beta-Tester Status
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isBetaTester 
                  ? 'User has beta-tester access (50 accounts, 5000 conversations by default)'
                  : 'User does not have beta-tester access'}
              </p>
            </div>
            <button
              onClick={onToggleBetaTester}
              disabled={updating}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                isBetaTester
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              } disabled:opacity-50`}
            >
              {updating 
                ? 'Updating...' 
                : isBetaTester 
                  ? 'Remove Beta-Tester' 
                  : 'Set as Beta-Tester'}
            </button>
          </div>
          {isBetaTester && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Beta-Tester Benefits:</strong>
              </p>
              <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 list-disc list-inside">
                <li>50 accounts limit (unless admin sets different)</li>
                <li>5000 conversations (unless admin sets different)</li>
                <li>Basic features enabled: Add Accounts, Automation</li>
                <li>Other features (Scraper, Mass DM) must be enabled manually by admin</li>
                <li>Account activated automatically</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Features</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canAddAccounts || false}
              onChange={(e) => setFeatures({ ...features, canAddAccounts: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canAddAccounts</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseAutomation || false}
              onChange={(e) => setFeatures({ ...features, canUseAutomation: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseAutomation</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseScraper || false}
              onChange={(e) => setFeatures({ ...features, canUseScraper: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseScraper</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseMassDM || false}
              onChange={(e) => setFeatures({ ...features, canUseMassDM: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseMassDM</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseMassLike || false}
              onChange={(e) => setFeatures({ ...features, canUseMassLike: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseMassLike</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseMassTasks || false}
              onChange={(e) => setFeatures({ ...features, canUseMassTasks: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseMassTasks</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseFUFMethod || false}
              onChange={(e) => setFeatures({ ...features, canUseFUFMethod: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseFUFMethod</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseActiveCheckers || false}
              onChange={(e) => setFeatures({ ...features, canUseActiveCheckers: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseActiveCheckers</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
            User can tag accounts as active_checker and use them in Scraper sessions for activity checking
          </p>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={features.canUseFollowBackCheckers || false}
              onChange={(e) => setFeatures({ ...features, canUseFollowBackCheckers: e.target.checked })}
              className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">canUseFollowBackCheckers</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
            User can tag accounts as follow_back_checker and use them in Follow-back automation scanning pool
          </p>
          <button
            onClick={onUpdateFeatures}
            disabled={updating}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {updating ? 'Updating...' : 'Update Features'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Limits</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Accounts (0 = unlimited)
            </label>
            <input
              type="number"
              value={limits.maxAccounts || 0}
              onChange={(e) => setLimits({ ...limits, maxAccounts: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min="0"
              placeholder="0"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Set to 0 for unlimited accounts
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Conversations (Deprecated - use billing system)
            </label>
            <input
              type="number"
              value={limits.maxConversations || 0}
              onChange={(e) => setLimits({ ...limits, maxConversations: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min="0"
              placeholder="0"
              disabled
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This field is deprecated. Use the Top-Up section below to manage conversations.
            </p>
          </div>
          <button
            onClick={onUpdateLimits}
            disabled={updating}
            className="mt-2 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {updating ? 'Updating...' : 'Update Limits'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Top-Up Conversations</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Current Balance:</strong> {conversations?.remaining?.toLocaleString() || 0} conversations remaining
            </p>
            {conversations?.monthlyAllowance > 0 && (
              <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                Monthly Allowance: {conversations.monthlyAllowance.toLocaleString()} • 
                Extra Balance: {conversations.extraBalance?.toLocaleString() || 0}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amount to Add
            </label>
            <input
              type="number"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min="1"
              placeholder="Enter number of conversations"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Number of conversations to add to user's balance
            </p>
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={addToExtras}
                onChange={(e) => setAddToExtras(e.target.checked)}
                className="mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Add to Extra Balance (persists through subscription resets)
              </span>
            </label>
          </div>
          <button
            onClick={() => {
              if (topUpAmount && parseInt(topUpAmount) > 0) {
                onTopUpConversations(topUpAmount, addToExtras);
                setTopUpAmount('');
                setAddToExtras(false);
              }
            }}
            disabled={updating || !topUpAmount || parseInt(topUpAmount) <= 0}
            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updating ? 'Adding...' : `Add ${topUpAmount || 0} Conversations`}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Change Password</h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-3 mb-4">
            <p className="text-sm text-orange-800 dark:text-orange-200">
              <strong>Warning:</strong> This will change the user's password. The user will need to use the new password to log in.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10"
                minLength={6}
                placeholder="Enter new password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              minLength={6}
              placeholder="Confirm new password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
            )}
          </div>
          <button
            onClick={() => {
              if (newPassword && newPassword.length >= 6 && newPassword === confirmPassword) {
                onChangePassword(newPassword);
                setNewPassword('');
                setConfirmPassword('');
              }
            }}
            disabled={updating || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updating ? 'Changing Password...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Revenue Tab Component
const RevenueTab = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users/revenue');
      if (response.data.success) {
        setRevenueData(response.data.revenue || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      toast.error('Failed to fetch revenue data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading revenue data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              ${summary.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Paying Users</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {summary.payingUsers} / {summary.totalUsers}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {summary.totalUsers > 0 ? Math.round((summary.payingUsers / summary.totalUsers) * 100) : 0}% conversion rate
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Revenue per User</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              ${summary.payingUsers > 0 ? (summary.totalRevenue / summary.payingUsers).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4 flex items-center justify-end">
        <button
          onClick={fetchRevenue}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {/* Revenue Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Package</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Subscriptions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Extras</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payments</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Payment</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {revenueData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No revenue data found
                  </td>
                </tr>
              ) : (
                revenueData.map((item) => (
                  <tr key={item.user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{item.user.username}</div>
                        {item.user.isBetaTester && (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                            BETA
                          </span>
                        )}
                        {item.user.isTrialUser && (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                            TRIAL
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{item.user.email}</div>
                    </td>
                    <td className="px-4 py-4">
                      {item.package.tier ? (
                        <div>
                          <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                            {item.package.tier}
                          </span>
                          {item.package.status && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {item.package.status === 'active' ? 'Active' : item.package.status}
                              {item.package.endDate && (
                                <div className="text-xs text-gray-400 dark:text-gray-500">
                                  Exp: {new Date(item.package.endDate).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">No package</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        ${item.revenue.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        ${item.revenue.subscription.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        ${item.revenue.extras.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {item.revenue.paymentCount}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {item.revenue.lastPaymentDate ? (
                        <div className="text-sm text-gray-900 dark:text-white">
                          {new Date(item.revenue.lastPaymentDate).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Never</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Packages Management Tab Component
const PackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInitialize, setShowInitialize] = useState(false);

  const [formData, setFormData] = useState({
    packageId: '',
    name: '',
    price: '',
    maxAccounts: '',
    monthlyConversations: '',
    pricePerExtra: '',
    isActive: true,
    description: ''
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await api.get('/packages');
      if (response.data.success) {
        setPackages(response.data.packages);
        setShowInitialize(response.data.packages.length === 0);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const initializePackages = async () => {
    setSaving(true);
    try {
      const response = await api.post('/packages/initialize');
      if (response.data.success) {
        toast.success('Default packages initialized');
        await loadPackages();
        setShowInitialize(false);
      }
    } catch (error) {
      console.error('Error initializing packages:', error);
      toast.error(error.response?.data?.error || 'Failed to initialize packages');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg._id);
    setFormData({
      packageId: pkg.packageId,
      name: pkg.name,
      price: pkg.price.toString(),
      maxAccounts: pkg.maxAccounts.toString(),
      monthlyConversations: pkg.monthlyConversations.toString(),
      pricePerExtra: pkg.pricePerExtra.toString(),
      isActive: pkg.isActive,
      description: pkg.description || ''
    });
  };

  const handleCancel = () => {
    setEditingPackage(null);
    setFormData({
      packageId: '',
      name: '',
      price: '',
      maxAccounts: '',
      monthlyConversations: '',
      pricePerExtra: '',
      isActive: true,
      description: ''
    });
  };

  const handleSave = async () => {
    if (!formData.packageId || !formData.name || !formData.price || 
        !formData.maxAccounts || !formData.monthlyConversations || !formData.pricePerExtra) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/packages/${formData.packageId}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        maxAccounts: parseInt(formData.maxAccounts),
        monthlyConversations: parseInt(formData.monthlyConversations),
        pricePerExtra: parseFloat(formData.pricePerExtra),
        isActive: formData.isActive,
        description: formData.description
      });

      if (response.data.success) {
        toast.success('Package updated successfully');
        await loadPackages();
        handleCancel();
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(error.response?.data?.error || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (packageId) => {
    if (!window.confirm('Are you sure you want to deactivate this package?')) {
      return;
    }

    try {
      const response = await api.delete(`/packages/${packageId}`);
      if (response.data.success) {
        toast.success('Package deactivated');
        await loadPackages();
      }
    } catch (error) {
      console.error('Error deactivating package:', error);
      toast.error(error.response?.data?.error || 'Failed to deactivate package');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600 dark:text-gray-400">Loading packages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Package Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage subscription packages, pricing, and limits
          </p>
        </div>
        {showInitialize && (
          <button
            onClick={initializePackages}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Initializing...' : 'Initialize Default Packages'}
          </button>
        )}
      </div>

      {packages.length === 0 && !showInitialize ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">No packages found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg._id}
              className={`bg-white dark:bg-gray-800 rounded-2xl shadow p-6 border-2 ${
                pkg.isActive ? 'border-gray-200 dark:border-gray-700' : 'border-red-300 dark:border-red-800'
              }`}
            >
              {editingPackage === pkg._id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Package ID
                      </label>
                      <input
                        type="text"
                        value={formData.packageId}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Price (USD) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Accounts *
                      </label>
                      <input
                        type="number"
                        value={formData.maxAccounts}
                        onChange={(e) => setFormData({ ...formData, maxAccounts: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Monthly Conversations *
                      </label>
                      <input
                        type="number"
                        value={formData.monthlyConversations}
                        onChange={(e) => setFormData({ ...formData, monthlyConversations: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Price per Extra Conversation *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.pricePerExtra}
                        onChange={(e) => setFormData({ ...formData, pricePerExtra: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-2xl focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`active-${pkg._id}`}
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-2 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`active-${pkg._id}`} className="text-sm text-gray-700 dark:text-gray-300">
                      Active (visible to users)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {pkg.name}
                        </h3>
                        {pkg.isActive ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-red-500" />
                        )}
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          pkg.isActive 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {pkg.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Package ID: {pkg.packageId}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(pkg)}
                        className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors"
                        title="Edit package"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      {pkg.isActive && (
                        <button
                          onClick={() => handleDeactivate(pkg.packageId)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                          title="Deactivate package"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Price</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        ${pkg.price.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">per month</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Max Accounts</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {pkg.maxAccounts.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Monthly Conversations</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {pkg.monthlyConversations.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Price per Extra</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        ${pkg.pricePerExtra.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {pkg.description && (
                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      {pkg.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const infraFormDefaults = {
  automationQueues: {
    inboxScanRateLimit: 500,
    dmReplyRateLimit: 200,
    fullScanRateLimit: 50,
    failedDMRetryRateLimit: 50,
    followBackScanRateLimit: 10,
    followBackDMRateLimit: 50,
    followBackUnfollowRateLimit: 20,
    followBackRetryRateLimit: 5
  },
  automationWorkers: {
    inboxScanConcurrency: 500,
    dmReplyConcurrency: 200,
    fullScanConcurrency: 50,
    failedDMRetryConcurrency: 50
  },
  massTaskWorkers: {
    massDMConcurrency: 50,
    massLikeConcurrency: 50,
    massFollowConcurrency: 50,
    followBackScanConcurrency: 50,
    followBackDMConcurrency: 200,
    followBackUnfollowConcurrency: 100,
    followBackRetryConcurrency: 50
  },
  scraperWorkers: {
    scrapingWorkerCount: 5,
    scrapingConcurrencyPerWorker: 1,
    countryCheckWorkers: 144,
    countryCheckConcurrency: 40,
    activityCheckWorkers: 144,
    activityCheckConcurrency: 40,
    activityCheckBullSlots: 0
  }
};

/** Follow-back only: purge BullMQ backlog (admin Infrastructure). */
function FollowBackQueuePurgeControls({ queues, disabled, busyKey, onPurge, isDarkMode, subText }) {
  if (!queues?.length) return null;
  const btnBase = `text-[10px] px-2 py-0.5 rounded border font-medium ${
    isDarkMode ? 'border-slate-600 hover:bg-slate-800 text-slate-200' : 'border-slate-300 hover:bg-slate-100 text-slate-800'
  } disabled:opacity-40`;
  return (
    <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-600/60' : 'border-slate-200/80'}`}>
      <div className={`text-[10px] font-semibold uppercase mb-1 ${subText}`}>Purge queue (follow-back Redis)</div>
      <p className={`text-[10px] mb-2 leading-snug ${subText}`}>
        Backlog removes waiting, delayed, and paused. Does not stop active jobs. Use “+ failed” to clear failed job
        records in Redis. Latest DM error text is stored on each FollowBackRecord (<code className="text-[9px]">dmError</code>).
      </p>
      <div className="flex flex-col gap-1.5">
        {queues.map((q) => (
          <div key={q.name} className="flex flex-wrap items-center gap-1">
            <span className="font-mono text-[10px] w-36 shrink-0">{q.name}</span>
            <button
              type="button"
              disabled={disabled}
              className={btnBase}
              onClick={() => onPurge(q.name, false)}
            >
              {busyKey === `${q.name}:b` ? '…' : 'Purge backlog'}
            </button>
            <button
              type="button"
              disabled={disabled}
              className={btnBase}
              onClick={() => onPurge(q.name, true)}
            >
              {busyKey === `${q.name}:f` ? '…' : '+ failed'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** V8 heap snapshot requests (automation replicas via Redis). Split from Infrastructure for clarity. */
const HeapSnapshotTab = () => {
  const { isDarkMode } = useTheme();
  const [heapSnapshotAdmin, setHeapSnapshotAdmin] = useState(null);
  const [heapReplicaId, setHeapReplicaId] = useState('');
  const [heapPid, setHeapPid] = useState('');
  const [heapRequestId, setHeapRequestId] = useState(null);
  const [heapResult, setHeapResult] = useState(null);
  const [heapBusy, setHeapBusy] = useState(false);
  const [heapError, setHeapError] = useState(null);
  const [tabError, setTabError] = useState(null);

  const loadHeapSnapshotFlags = useCallback(async () => {
    try {
      const res = await api.get('/admin/system/infrastructure');
      if (!res.data.success) return;
      setHeapSnapshotAdmin(res.data.heapSnapshotAdmin || null);
      setTabError(null);
    } catch (e) {
      setTabError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    loadHeapSnapshotFlags();
    const id = setInterval(loadHeapSnapshotFlags, 8000);
    return () => clearInterval(id);
  }, [loadHeapSnapshotFlags]);

  useEffect(() => {
    if (!heapRequestId) return undefined;
    let cancelled = false;
    let polls = 0;
    const poll = async () => {
      polls += 1;
      if (polls > 500) {
        if (!cancelled) {
          setHeapError('Timed out waiting for automation replica.');
          setHeapRequestId(null);
        }
        return;
      }
      try {
        const res = await api.get(`/admin/system/heap-snapshot/${encodeURIComponent(heapRequestId)}`);
        if (cancelled || !res.data?.success) return;
        if (!res.data.pending && res.data.result) {
          setHeapResult(res.data.result);
          setHeapRequestId(null);
        }
      } catch (e) {
        if (!cancelled) setHeapError(e.response?.data?.error || e.message);
      }
    };
    poll();
    const interval = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [heapRequestId]);

  const requestHeapSnapshot = async () => {
    setHeapError(null);
    setHeapResult(null);
    const rid = heapReplicaId.trim();
    if (!rid) {
      setHeapError('Enter replica id (from Infrastructure → Node.js process memory: text before " · ").');
      return;
    }
    const body = { replicaId: rid };
    const p = heapPid.trim();
    if (p) {
      const n = parseInt(p, 10);
      if (Number.isFinite(n)) body.pid = n;
    }
    try {
      setHeapBusy(true);
      const res = await api.post('/admin/system/heap-snapshot', body);
      if (!res.data?.success) {
        setHeapError(res.data?.error || 'Request failed');
        return;
      }
      setHeapRequestId(res.data.requestId);
    } catch (e) {
      setHeapError(e.response?.data?.error || e.message);
    } finally {
      setHeapBusy(false);
    }
  };

  const cardClass = `rounded-2xl border shadow-sm ${
    isDarkMode
      ? 'border-slate-700/80 bg-slate-900/70 text-slate-100'
      : 'border-slate-200/90 bg-white text-slate-900'
  }`;
  const subText = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-6">
      {tabError && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Could not load flags: {tabError}
        </div>
      )}

      <div className={`${cardClass} p-6`}>
        <h2 className="text-lg font-bold mb-2">V8 heap snapshot (automation replica)</h2>
        <p className={`text-sm mb-4 ${subText}`}>
          Copy <strong>Replica · PID</strong> from the <strong>Infrastructure</strong> tab (Node.js process memory table)
          for the automation row you want. Sends a Redis message so the <strong>matching</strong> automation Node process
          runs <code className="text-xs">v8.writeHeapSnapshot</code>. That process <strong>blocks</strong> until the file
          is written (can be minutes on large heaps). The file path is on that container — copy via your host shell (e.g.{' '}
          Railway <code className="text-xs">run</code>) before redeploy. Requires{' '}
          <code className="text-xs">ADMIN_HEAP_SNAPSHOT_ENABLED=true</code> on your <strong>main</strong> service (admin
          HTTP) and on each <strong>automation</strong> replica, plus automation Redis.{' '}
          <code className="text-xs">SERVICE_TYPE=api</code> is not required — <code className="text-xs">main</code> is
          enough.
        </p>
        {!heapSnapshotAdmin?.enabled ? (
          <p className={`text-sm ${subText}`}>
            Disabled — set <code className="text-xs">ADMIN_HEAP_SNAPSHOT_ENABLED=true</code> to show controls.
            {heapSnapshotAdmin && !heapSnapshotAdmin.automationRedisConfigured ? (
              <span className="block mt-2 text-amber-600 dark:text-amber-400">
                Automation Redis is not configured on this process — cannot publish requests (set REDIS_URL_AUTOMATION or
                REDIS_URL on main so admin can reach the automation Redis).
              </span>
            ) : null}
          </p>
        ) : (
          <div className="space-y-3">
            {!heapSnapshotAdmin.automationRedisConfigured ? (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Automation Redis is not configured on this process — snapshot requests cannot be published.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3 items-end">
              <label className="block min-w-[200px]">
                <span className={`block text-xs font-semibold mb-1 ${subText}`}>Replica id</span>
                <input
                  type="text"
                  value={heapReplicaId}
                  onChange={(e) => setHeapReplicaId(e.target.value)}
                  placeholder="e.g. Railway replica id"
                  className={`w-full rounded-lg border px-3 py-2 text-sm font-mono ${
                    isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-300 bg-white'
                  }`}
                />
              </label>
              <label className="block w-28">
                <span className={`block text-xs font-semibold mb-1 ${subText}`}>PID (opt.)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={heapPid}
                  onChange={(e) => setHeapPid(e.target.value)}
                  placeholder="pid"
                  className={`w-full rounded-lg border px-3 py-2 text-sm font-mono ${
                    isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-300 bg-white'
                  }`}
                />
              </label>
              <button
                type="button"
                disabled={heapBusy || !!heapRequestId}
                onClick={requestHeapSnapshot}
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {heapBusy ? 'Sending…' : heapRequestId ? 'Waiting for replica…' : 'Request snapshot'}
              </button>
            </div>
            {heapError && <div className="text-sm text-red-600 dark:text-red-400">{heapError}</div>}
            {heapRequestId && (
              <p className={`text-sm font-mono ${subText}`}>
                Polling request <span className="text-slate-700 dark:text-slate-200">{heapRequestId}</span>…
              </p>
            )}
            {heapResult && (
              <div
                className={`rounded-lg border p-3 text-sm font-mono whitespace-pre-wrap ${
                  heapResult.ok
                    ? isDarkMode
                      ? 'border-emerald-700/50 bg-emerald-950/30 text-emerald-100'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-900'
                    : isDarkMode
                      ? 'border-red-700/50 bg-red-950/30 text-red-100'
                      : 'border-red-200 bg-red-50 text-red-900'
                }`}
              >
                {heapResult.ok ? (
                  <>
                    ok · {heapResult.sizeBytes != null ? `${Math.round(heapResult.sizeBytes / 1024 / 1024)} MB` : ''}
                    {'\n'}
                    {heapResult.path || ''}
                  </>
                ) : (
                  heapResult.error || 'failed'
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Live ops: cross-service heartbeats, CycleTLS, BullMQ counts, DB worker settings. */
const InfrastructureTab = () => {
  const { isDarkMode } = useTheme();
  const [overview, setOverview] = useState(null);
  const [cycleTls, setCycleTls] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [infraError, setInfraError] = useState(null);
  const [poolInput, setPoolInput] = useState('');
  const [poolTarget, setPoolTarget] = useState('');
  const [purgingFollowBack, setPurgingFollowBack] = useState(null);
  const [savingPool, setSavingPool] = useState(false);
  const [savingWorkers, setSavingWorkers] = useState(false);
  const [formData, setFormData] = useState(infraFormDefaults);

  const loadInfrastructure = useCallback(async () => {
    try {
      const res = await api.get('/admin/system/infrastructure');
      if (!res.data.success) return;
      setOverview(res.data.overview || null);
      setCycleTls(res.data.cycleTls || null);
      setQueueStats(res.data.queueStats || null);
      setInfraError(null);
    } catch (e) {
      setInfraError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    loadInfrastructure();
    const id = setInterval(loadInfrastructure, 4000);
    return () => clearInterval(id);
  }, [loadInfrastructure]);

  const fetchWorkerSettings = useCallback(async () => {
    try {
      const response = await api.get('/admin/system-settings');
      if (!response.data.success) return;
      const s = response.data.settings;
      setFormData({
        automationQueues: { ...infraFormDefaults.automationQueues, ...s.automationQueues },
        automationWorkers: { ...infraFormDefaults.automationWorkers, ...s.automationWorkers },
        massTaskWorkers: { ...infraFormDefaults.massTaskWorkers, ...s.massTaskWorkers },
        scraperWorkers: {
          ...infraFormDefaults.scraperWorkers,
          ...(s.scraperWorkers || {}),
          activityCheckBullSlots: s.scraperWorkers?.activityCheckBullSlots ?? 0
        }
      });
    } catch (e) {
      toast.error('Failed to load worker settings');
    }
  }, []);

  useEffect(() => {
    fetchWorkerSettings();
  }, [fetchWorkerSettings]);

  const handleInfraChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseInt(value, 10) || 0
      }
    }));
  };

  const handleSaveWorkers = async () => {
    try {
      setSavingWorkers(true);
      const scraperWorkers = {
        ...formData.scraperWorkers,
        activityCheckBullSlots:
          formData.scraperWorkers?.activityCheckBullSlots > 0
            ? formData.scraperWorkers.activityCheckBullSlots
            : null
      };
      await api.put('/admin/system-settings', { settings: { ...formData, scraperWorkers } });
      toast.success('Saved — automation workers reload via cache; scraper replicas poll DB ~20s');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save');
    } finally {
      setSavingWorkers(false);
    }
  };

  const handleApplyPool = async () => {
    const n = parseInt(poolInput, 10);
    if (!Number.isFinite(n)) {
      toast.error('Enter a number (50–5000)');
      return;
    }
    try {
      setSavingPool(true);
      const body = { poolSize: n };
      if (poolTarget) body.serviceType = poolTarget;
      await api.patch('/admin/system/cycle-tls-pool', body);
      toast.success('CycleTLS pool cap command sent');
      await loadInfrastructure();
      setPoolInput('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Pool update failed');
    } finally {
      setSavingPool(false);
    }
  };

  const handlePurgeFollowBackQueue = async (queueName, removeFailed) => {
    const msg = removeFailed
      ? `Remove waiting/delayed/paused jobs and clear FAILED history for "${queueName}"? Active jobs are not removed.`
      : `Remove waiting, delayed, and paused jobs from "${queueName}"? Active jobs are not removed.`;
    if (!window.confirm(msg)) return;
    try {
      setPurgingFollowBack(`${queueName}:${removeFailed ? 'f' : 'b'}`);
      const res = await api.post('/admin/system/followback-queue/purge', {
        queueName,
        removeFailed,
        removeCompleted: false
      });
      if (res.data?.success) {
        toast.success(
          `Purged ${queueName}: backlog ${res.data.removedBacklog ?? 0}, failed cleaned ${res.data.cleanedFailed ?? 0}`
        );
      }
      await loadInfrastructure();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setPurgingFollowBack(null);
    }
  };

  const cardClass = `rounded-2xl border shadow-sm ${
    isDarkMode
      ? 'border-slate-700/80 bg-slate-900/70 text-slate-100'
      : 'border-slate-200/90 bg-white text-slate-900'
  }`;

  const subText = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const labelClass = `text-xs font-semibold uppercase tracking-wide ${subText}`;

  const poolRows = useMemo(() => {
    if (!cycleTls || cycleTls.error) return [];
    if (cycleTls.cycleTlsInstances && cycleTls.cycleTlsInstances.length > 0) {
      return cycleTls.cycleTlsInstances;
    }
    if (cycleTls.cap != null) {
      return [
        {
          nodeServiceType: cycleTls.nodeServiceType || 'local',
          pid: cycleTls.pid,
          replicaId: cycleTls.replicaId,
          cap: cycleTls.cap,
          created: cycleTls.created,
          inUse: cycleTls.inUse,
          idle: cycleTls.idle,
          utilizationOfCapPercent: cycleTls.utilizationOfCapPercent
        }
      ];
    }
    return [];
  }, [cycleTls]);

  const aggUtil =
    cycleTls && !cycleTls.error && typeof cycleTls.utilizationOfCapPercent === 'number'
      ? cycleTls.utilizationOfCapPercent
      : null;

  const serviceRows = overview?.serviceRows || [];
  const queueRealms = queueStats?.realms || [];

  const fmtInfraMb = (bytes) => {
    if (bytes == null || !Number.isFinite(Number(bytes))) return '—';
    const mb = Number(bytes) / (1024 * 1024);
    return mb >= 100 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
  };

  const fmtInfraNativeApprox = (nm) => {
    if (!nm || nm.rssBytes == null || !Number.isFinite(Number(nm.rssBytes))) return '—';
    const rss = Number(nm.rssBytes);
    const heap = Number(nm.heapUsedBytes) || 0;
    return fmtInfraMb(Math.max(0, rss - heap));
  };

  const heapSpaceUsedBytes = (spaces, name) => {
    if (!Array.isArray(spaces)) return null;
    const s = spaces.find((x) => x.spaceName === name);
    return s && s.usedBytes != null ? Number(s.usedBytes) : null;
  };

  /** One row per replica PID that reported `nodeProcessMemory` in its heartbeat. */
  const nodeMemoryRows = useMemo(() => {
    const rows = [];
    for (const row of serviceRows) {
      for (const hb of row.heartbeats || []) {
        const nm = hb.nodeProcessMemory;
        if (!nm || nm.rssBytes == null) continue;
        rows.push({
          serviceType: row.serviceType,
          replicaId: hb.replicaId,
          pid: hb.pid,
          hostname: hb.hostname,
          nodeVersion: hb.nodeVersion,
          nodeProcessMemory: nm,
          v8HeapSpaces: hb.v8HeapSpaces,
          nodeRuntimeCounters: hb.nodeRuntimeCounters,
          updatedAt: hb.updatedAt
        });
      }
    }
    rows.sort((a, b) => (b.nodeProcessMemory.rssBytes || 0) - (a.nodeProcessMemory.rssBytes || 0));
    return rows;
  }, [serviceRows]);

  const fmtInfraCounter = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return '—';
    return String(v);
  };

  const fmtQ = (v) => (v == null ? '—' : v);

  return (
    <div className="space-y-6">
      {infraError && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Infrastructure: {infraError}
        </div>
      )}

      <div
        className={`relative overflow-hidden rounded-2xl p-6 ${
          isDarkMode
            ? 'bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 border border-indigo-500/20'
            : 'bg-gradient-to-br from-slate-50 via-indigo-50 to-sky-50 border border-indigo-200/60'
        }`}
      >
        <div className={labelClass}>Fleet summary</div>
        <p className={`text-sm mt-1 mb-4 ${subText}`}>
          Per-container CPU/RAM from Redis heartbeats (cgroup limits when available, else OS). BullMQ counts use
          the same Redis realms as workers.
        </p>
        {aggUtil != null && (
          <div className={`mb-4 pb-4 border-b ${isDarkMode ? 'border-indigo-500/20' : 'border-indigo-200/50'}`}>
            <div className={labelClass}>Fleet CycleTLS utilization (in-use / cap)</div>
            <div className="mt-2 h-3 rounded-full overflow-hidden bg-black/10 dark:bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 transition-all duration-500"
                style={{ width: `${Math.min(100, aggUtil)}%` }}
              />
            </div>
            <div className={`mt-1 text-sm font-medium ${subText}`}>{aggUtil}% aggregate</div>
          </div>
        )}
      </div>

      <div className={`${cardClass} p-6`}>
        <h2 className="text-lg font-bold mb-2">Node.js process memory</h2>
        <p className={`text-sm mb-4 ${subText}`}>
          Each row is one running Node process (replica × PID), sorted by <strong>RSS</strong> (resident set — includes
          native memory such as CycleTLS and WebSockets). <strong>Heap used</strong> is the V8 JavaScript heap only.
          “Est. native” is RSS minus heap used (rough split, not exact). <strong>V8 heap spaces</strong> are{' '}
          <code className="text-xs">space_used_size</code> per space (grow correlates with JS objects).{' '}
          <strong>Runtime counters</strong> help tie high heap to active automations, XChat WebSockets, CycleTLS pool,
          and BullMQ <code className="text-xs">automation-start</code> (this process only). Values refresh every 4s.
        </p>
        {nodeMemoryRows.length === 0 ? (
          <p className={`text-sm ${subText}`}>
            No Node memory samples yet — replicas must publish heartbeats with <code className="text-xs">nodeProcessMemory</code>{' '}
            (automation / scraper / main web service on latest deploy).
          </p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={`text-left border-b ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                  <th className="py-2 pr-3 font-semibold">Service</th>
                  <th className="py-2 pr-3 font-semibold">Replica · PID</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">RSS</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Heap used</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Heap total</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Est. native</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">External</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">ArrayBuffers</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">V8 limit</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Old space</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Large obj</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Code space</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">Act. auto</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">XChat WS</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">XChat trk</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">TLS in / cap</th>
                  <th className="py-2 pr-3 font-semibold tabular-nums">BM start A/W</th>
                </tr>
              </thead>
              <tbody>
                {nodeMemoryRows.map((r) => {
                  const nm = r.nodeProcessMemory;
                  const rc = r.nodeRuntimeCounters || {};
                  const tlsIn = rc.cycleTlsInUse;
                  const tlsCap = rc.cycleTlsCap;
                  const tlsCell =
                    tlsIn != null && tlsCap != null
                      ? `${fmtInfraCounter(tlsIn)} / ${fmtInfraCounter(tlsCap)}`
                      : tlsIn != null
                        ? fmtInfraCounter(tlsIn)
                        : '—';
                  const bmA = rc.bullmqAutomationStartActive;
                  const bmW = rc.bullmqAutomationStartWaiting;
                  const bmCell =
                    bmA != null || bmW != null ? `${fmtInfraCounter(bmA)} / ${fmtInfraCounter(bmW)}` : '—';
                  return (
                    <tr
                      key={`${r.serviceType}-${r.replicaId}-${r.pid}`}
                      className={`border-t ${isDarkMode ? 'border-slate-700/80' : 'border-slate-100'}`}
                    >
                      <td className="py-2 pr-3 capitalize font-medium">{r.serviceType}</td>
                      <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">
                        {(r.replicaId || r.hostname || '—') + ` · ${r.pid}`}
                      </td>
                      <td className="py-2 pr-3 tabular-nums font-semibold">{fmtInfraMb(nm.rssBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(nm.heapUsedBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(nm.heapTotalBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums text-amber-700 dark:text-amber-300">{fmtInfraNativeApprox(nm)}</td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(nm.externalBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(nm.arrayBuffersBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums text-slate-500 dark:text-slate-400">{fmtInfraMb(nm.heapSizeLimitBytes)}</td>
                      <td className="py-2 pr-3 tabular-nums text-violet-700 dark:text-violet-300">
                        {fmtInfraMb(heapSpaceUsedBytes(r.v8HeapSpaces, 'old_space'))}
                      </td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(heapSpaceUsedBytes(r.v8HeapSpaces, 'large_object_space'))}</td>
                      <td className="py-2 pr-3 tabular-nums">{fmtInfraMb(heapSpaceUsedBytes(r.v8HeapSpaces, 'code_space'))}</td>
                      <td className="py-2 pr-3 tabular-nums font-mono text-xs">{fmtInfraCounter(rc.activeAutomations)}</td>
                      <td className="py-2 pr-3 tabular-nums font-mono text-xs">{fmtInfraCounter(rc.xchatWsOpenConnections)}</td>
                      <td className="py-2 pr-3 tabular-nums font-mono text-xs">{fmtInfraCounter(rc.xchatWsTrackedAccounts)}</td>
                      <td className="py-2 pr-3 tabular-nums font-mono text-xs">{tlsCell}</td>
                      <td className="py-2 pr-3 tabular-nums font-mono text-xs">{bmCell}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className={`text-xs mt-3 ${subText}`}>
          To capture a <code className="text-xs">.heapsnapshot</code> on an automation replica, use the{' '}
          <strong>Heap snapshot</strong> tab (same replica id as the first column here; optional PID disambiguates).
        </p>
      </div>

      <div className={`${cardClass} p-6`}>
        <h2 className="text-lg font-bold mb-4">Live per service</h2>
        {serviceRows.length === 0 ? (
          <p className={`text-sm ${subText}`}>No overview yet — check Redis (REDIS_URL) and redeploy workers so they connect Redis.</p>
        ) : (
          <div className="space-y-4">
            {serviceRows.map((row) => (
              <div
                key={row.serviceType}
                className={`rounded-xl border p-4 ${
                  isDarkMode ? 'border-slate-600 bg-slate-800/40' : 'border-slate-200 bg-slate-50/90'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-base font-bold capitalize">{row.serviceType}</h3>
                  {row.queueRealm && row.queueRealm.available && (
                    <div className={`text-xs font-mono ${subText}`}>
                      queues: w {row.queueRealm.totals?.waiting ?? 0} · d {row.queueRealm.totals?.delayed ?? 0} · a{' '}
                      {row.queueRealm.totals?.active ?? 0} · f {row.queueRealm.totals?.failed ?? 0}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <div className={`text-xs font-semibold uppercase ${subText} mb-2`}>Host / heartbeat</div>
                    {row.heartbeats.length === 0 ? (
                      <p className={`text-sm ${subText}`}>No heartbeat (service not running or stale).</p>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {row.heartbeats.map((hb) => (
                          <li
                            key={`${hb.replicaId}-${hb.pid}`}
                            className={`rounded-lg px-3 py-2 border ${
                              isDarkMode ? 'border-slate-600 bg-slate-900/50' : 'border-slate-200 bg-white'
                            }`}
                          >
                            <div className="font-mono text-xs text-slate-500 dark:text-slate-400 truncate" title={hb.hostname}>
                              {hb.replicaId || hb.hostname} · pid {hb.pid}
                            </div>
                            <div className="mt-1 tabular-nums">
                              Limit {hb.totalRAMGB} GB
                              {hb.usedRAMGB != null ? ` · used ${hb.usedRAMGB} GB` : ''} · free {hb.freeRAMGB} GB · CPUs{' '}
                              {hb.cpuCores}
                            </div>
                            {hb.resourceSource ? (
                              <div className="mt-0.5 text-[10px] font-mono text-slate-400 dark:text-slate-500 truncate" title={hb.resourceSource}>
                                {hb.resourceSource}
                              </div>
                            ) : null}
                            {hb.nodeProcessMemory && hb.nodeProcessMemory.rssBytes != null ? (
                              <div
                                className={`mt-2 pt-2 border-t text-xs tabular-nums ${
                                  isDarkMode ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-700'
                                }`}
                              >
                                <div className={`font-semibold uppercase tracking-wide ${subText} mb-0.5`}>Node process</div>
                                RSS {fmtInfraMb(hb.nodeProcessMemory.rssBytes)} · heap {fmtInfraMb(hb.nodeProcessMemory.heapUsedBytes)} /{' '}
                                {fmtInfraMb(hb.nodeProcessMemory.heapTotalBytes)} · est. native ~{fmtInfraNativeApprox(hb.nodeProcessMemory)}
                              </div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className={`text-xs font-semibold uppercase ${subText} mb-2`}>CycleTLS pool</div>
                    {row.cycleTlsInstances.length === 0 ? (
                      <p className={`text-sm ${subText}`}>No pool on this service type (or not reporting).</p>
                    ) : (
                      <ul className="space-y-2">
                        {row.cycleTlsInstances.map((tls, idx) => {
                          const u = Number(tls.utilizationOfCapPercent) || 0;
                          return (
                            <li
                              key={`${tls.replicaId}-${tls.pid}-${idx}`}
                              className={`rounded-lg px-3 py-2 border ${
                                isDarkMode ? 'border-slate-600 bg-slate-900/50' : 'border-slate-200 bg-white'
                              }`}
                            >
                              <div className="text-xs font-mono text-slate-500 dark:text-slate-400">
                                {tls.replicaId ? `${tls.replicaId} · ` : ''}pid {tls.pid ?? '—'}
                              </div>
                              <div className="text-sm tabular-nums mt-1">
                                in-use <span className="font-semibold">{tls.inUse}</span> / cap{' '}
                                <span className="font-semibold">{tls.cap}</span> · {u}%
                              </div>
                              <div className="mt-1 h-1.5 rounded-full overflow-hidden bg-black/10 dark:bg-white/10">
                                <div className="h-full bg-emerald-500/90 rounded-full" style={{ width: `${Math.min(100, u)}%` }} />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className={`text-xs font-semibold uppercase ${subText} mb-2`}>BullMQ (live)</div>
                    {!row.queueRealm && (
                      <p className={`text-sm ${subText}`}>
                        The <strong>main</strong> service enqueues into automation / scraper Redis — see “All queues by
                        Redis realm” below.
                      </p>
                    )}
                    {row.queueRealm && !row.queueRealm.available && (
                      <p className="text-sm text-amber-600 dark:text-amber-400">{row.queueRealm.error || 'Redis unavailable'}</p>
                    )}
                    {row.queueRealm && row.queueRealm.available && (
                      <div className="overflow-x-auto max-h-48 overflow-y-auto text-xs">
                        <table className="min-w-full">
                          <thead>
                            <tr className={`text-left ${subText}`}>
                              <th className="pr-2 py-1">Queue</th>
                              <th className="pr-2">Wait</th>
                              <th className="pr-2">Del</th>
                              <th className="pr-2">Act</th>
                              <th className="pr-2">Fail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(row.queueRealm.queues || []).map((q) => (
                              <tr key={q.name} className="border-t border-slate-200/50 dark:border-slate-600/50">
                                <td className="py-1 pr-2 font-mono whitespace-nowrap">{q.name}</td>
                                <td>{fmtQ(q.waiting)}</td>
                                <td>{fmtQ(q.delayed)}</td>
                                <td>{fmtQ(q.active)}</td>
                                <td>{fmtQ(q.failed)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {String(row.serviceType).toLowerCase() === 'followback' && (
                          <FollowBackQueuePurgeControls
                            queues={row.queueRealm.queues || []}
                            disabled={!!purgingFollowBack}
                            busyKey={purgingFollowBack}
                            onPurge={handlePurgeFollowBackQueue}
                            isDarkMode={isDarkMode}
                            subText={subText}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${cardClass} p-6`}>
        <h2 className="text-lg font-bold mb-2">All queues by Redis realm</h2>
        <p className={`text-sm mb-4 ${subText}`}>Job counts from the same Redis URLs as <code className="text-xs">REDIS_URL_*</code> realms.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {queueRealms.map((realm) => (
            <div
              key={realm.realm}
              className={`rounded-xl border p-4 ${
                isDarkMode ? 'border-slate-600 bg-slate-800/30' : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="font-bold capitalize mb-2">{realm.realm}</div>
              {!realm.available && (
                <p className="text-sm text-amber-600 dark:text-amber-400">{realm.error || 'Unavailable'}</p>
              )}
              {realm.available && (
                <>
                  <div className={`text-xs mb-2 ${subText}`}>
                    Σ wait {realm.totals?.waiting} · delayed {realm.totals?.delayed} · active {realm.totals?.active} ·
                    failed {realm.totals?.failed} · paused {realm.totals?.paused}
                  </div>
                  <div className="overflow-x-auto text-xs max-h-56 overflow-y-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className={`text-left ${subText}`}>
                          <th className="pr-2 py-1">Queue</th>
                          <th className="pr-2">Wait</th>
                          <th className="pr-2">Del</th>
                          <th className="pr-2">Act</th>
                          <th className="pr-2">Fail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(realm.queues || []).map((q) => (
                          <tr key={q.name} className="border-t border-slate-200/50 dark:border-slate-600/50">
                            <td className="py-1 pr-2 font-mono">{q.name}</td>
                            <td>{fmtQ(q.waiting)}</td>
                            <td>{fmtQ(q.delayed)}</td>
                            <td>{fmtQ(q.active)}</td>
                            <td>{fmtQ(q.failed)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {realm.realm === 'followback' && (
                      <FollowBackQueuePurgeControls
                        queues={realm.queues || []}
                        disabled={!!purgingFollowBack}
                        busyKey={purgingFollowBack}
                        onPurge={handlePurgeFollowBackQueue}
                        isDarkMode={isDarkMode}
                        subText={subText}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className={`${cardClass} p-6 space-y-4`}>
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-bold">Workers &amp; queues</h2>
            <button
              type="button"
              onClick={fetchWorkerSettings}
              className={`text-sm px-3 py-1 rounded-lg border ${
                isDarkMode ? 'border-slate-600 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              Reload from DB
            </button>
          </div>
          <p className={`text-sm ${subText}`}>
            Same persisted settings as the Settings tab. Values apply as workers reload system settings from the database.
          </p>

          <div>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              Automation workers (per instance)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['inboxScanConcurrency', 'Inbox scan'],
                ['dmReplyConcurrency', 'DM reply'],
                ['fullScanConcurrency', 'Full scan'],
                ['failedDMRetryConcurrency', 'Failed DM retry']
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={`block text-xs mb-1 ${subText}`}>{label}</label>
                  <input
                    type="number"
                    value={formData.automationWorkers[key]}
                    onChange={(e) => handleInfraChange('automationWorkers', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              Mass task workers
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['massDMConcurrency', 'Mass DM'],
                ['massLikeConcurrency', 'Mass like'],
                ['massFollowConcurrency', 'Mass follow']
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={`block text-xs mb-1 ${subText}`}>{label}</label>
                  <input
                    type="number"
                    value={formData.massTaskWorkers[key]}
                    onChange={(e) => handleInfraChange('massTaskWorkers', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              Follow-back workers
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['followBackScanConcurrency', 'Scan'],
                ['followBackDMConcurrency', 'DM'],
                ['followBackUnfollowConcurrency', 'Unfollow'],
                ['followBackRetryConcurrency', 'Retry']
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={`block text-xs mb-1 ${subText}`}>{label}</label>
                  <input
                    type="number"
                    value={formData.massTaskWorkers[key]}
                    onChange={(e) => handleInfraChange('massTaskWorkers', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              Follow-back queue rate limits (global)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['followBackScanRateLimit', 'Scan / sec'],
                ['followBackDMRateLimit', 'DM / sec'],
                ['followBackUnfollowRateLimit', 'Unfollow / sec'],
                ['followBackRetryRateLimit', 'Retry / sec']
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={`block text-xs mb-1 ${subText}`}>{label}</label>
                  <input
                    type="number"
                    value={formData.automationQueues[key]}
                    onChange={(e) => handleInfraChange('automationQueues', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              Scraper service (<code className="text-[10px]">SERVICE_TYPE=scraper</code> replicas)
            </h3>
            <p className={`text-xs mb-3 ${subText}`}>
              Persisted in DB. Each scraper process polls ~every 20s and restarts BullMQ workers when these values
              change (scraping, country-check, activity-check queues).
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['scrapingWorkerCount', 'Scraping worker processes'],
                ['scrapingConcurrencyPerWorker', 'Scraping concurrency / worker'],
                ['countryCheckWorkers', 'Country-check worker processes'],
                ['countryCheckConcurrency', 'Country-check concurrency / worker'],
                ['activityCheckWorkers', 'Activity-check worker processes'],
                ['activityCheckConcurrency', 'Activity-check concurrency / worker'],
                ['activityCheckBullSlots', 'Activity total slots (0 = off)']
              ].map(([key, label]) => (
                <div key={key} className={key === 'activityCheckBullSlots' ? 'col-span-2' : ''}>
                  <label className={`block text-xs mb-1 ${subText}`}>{label}</label>
                  <input
                    type="number"
                    value={formData.scraperWorkers[key]}
                    onChange={(e) => handleInfraChange('scraperWorkers', key, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleSaveWorkers}
              disabled={savingWorkers}
              className="px-5 py-2 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {savingWorkers ? 'Saving…' : 'Save worker & queue settings'}
            </button>
          </div>
        </div>

        <div className={`${cardClass} p-6 space-y-4`}>
          <h2 className="text-lg font-bold">CycleTLS pools (live)</h2>
          <p className={`text-sm ${subText}`}>
            Cap uses the same 50–5000 rules as <code className="text-xs">CYCLE_TLS_POOL_SIZE</code> /{' '}
            <code className="text-xs">SERVICE_TYPE=scraper</code> defaults. Target a service to fan-out over Redis when
            split.
          </p>

          {cycleTls?.error && (
            <div className="text-amber-600 dark:text-amber-400 text-sm">Metrics: {cycleTls.error}</div>
          )}

          <div className="space-y-3">
            {poolRows.length === 0 && !cycleTls?.error && (
              <div className={`text-sm ${subText}`}>No pool metrics yet (ensure Redis and workers are running).</div>
            )}
            {poolRows.map((row, i) => {
              const u = Number(row.utilizationOfCapPercent) || 0;
              return (
                <div
                  key={`${row.nodeServiceType}-${row.replicaId || ''}-${row.pid ?? i}`}
                  className={`rounded-xl p-4 border ${
                    isDarkMode ? 'border-slate-600 bg-slate-800/50' : 'border-slate-200 bg-slate-50/80'
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-semibold capitalize">{String(row.nodeServiceType || 'process')}</div>
                      <div className={`text-xs ${subText}`}>
                        {row.replicaId ? `${row.replicaId} · ` : ''}pid {row.pid ?? '—'}
                      </div>
                    </div>
                    <div className="text-right text-sm tabular-nums">
                      <div>
                        in-use <span className="font-bold">{row.inUse}</span> / cap{' '}
                        <span className="font-bold">{row.cap}</span>
                      </div>
                      <div className={subText}>created {row.created} · idle {row.idle}</div>
                    </div>
                  </div>
                  <div className="mt-2 h-2 rounded-full overflow-hidden bg-black/10 dark:bg-white/10">
                    <div
                      className="h-full rounded-full bg-emerald-500/90"
                      style={{ width: `${Math.min(100, u)}%` }}
                    />
                  </div>
                  <div className={`text-xs mt-1 ${subText}`}>{u}% of cap</div>
                </div>
              );
            })}
          </div>

          <div className={`rounded-xl p-4 border ${isDarkMode ? 'border-indigo-500/30 bg-indigo-950/30' : 'border-indigo-200 bg-indigo-50/50'}`}>
            <div className={labelClass}>Apply new pool cap (live)</div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <input
                type="number"
                min={50}
                max={5000}
                placeholder="50 – 5000"
                value={poolInput}
                onChange={(e) => setPoolInput(e.target.value)}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-600 text-white'
                    : 'bg-white border-slate-200 text-slate-900'
                }`}
              />
              <select
                value={poolTarget}
                onChange={(e) => setPoolTarget(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-600 text-white'
                    : 'bg-white border-slate-200 text-slate-900'
                }`}
              >
                <option value="">This process only</option>
                <option value="all">all (monolith)</option>
                <option value="main">main (or legacy api)</option>
                <option value="automation">automation</option>
                <option value="scraper">scraper</option>
                <option value="massdm">massdm</option>
                <option value="followback">followback</option>
              </select>
              <button
                type="button"
                onClick={handleApplyPool}
                disabled={savingPool}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
              >
                {savingPool ? 'Applying…' : 'Apply cap'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Processes Tab Component
const SystemProcessesTab = ({ socket }) => {
  const [services, setServices] = useState([]); // Grouped by service
  const [totalCount, setTotalCount] = useState(0);
  const [systemInfo, setSystemInfo] = useState(null);
  const [cycleTls, setCycleTls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'relevant', 'chrome', 'node' (kept for future use)
  const [sortBy, setSortBy] = useState('cpu'); // 'cpu', 'mem', 'pid'
  const [expandedServices, setExpandedServices] = useState(new Set()); // Track expanded service sections

  useEffect(() => {
    if (!socket) return;

    // Join room when component mounts
    socket.emit('join-system-processes');

    // Listen for updates
    const handleUpdate = (data) => {
      setServices(data.services || []); // Grouped by service
      setTotalCount(data.totalCount || 0);
      setSystemInfo(data.systemInfo || null);
      setCycleTls(data.cycleTls || null);
      setLastUpdate(new Date(data.timestamp || Date.now()));
      setLoading(false);
    };

    socket.on('system-processes-update', handleUpdate);

    // Fetch initial data
    const fetchInitial = async () => {
      try {
        const response = await api.get('/admin/system/processes');
        if (response.data.success) {
          handleUpdate(response.data);
        }
      } catch (error) {
        console.error('Error fetching system processes:', error);
        setLoading(false);
      }
    };

    fetchInitial();

    return () => {
      socket.off('system-processes-update', handleUpdate);
      socket.emit('leave-system-processes');
    };
  }, [socket]);

  // Note: Filter and sort are now handled within each service section
  // The old filteredAndSorted logic is no longer needed since we group by service

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading system processes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Processes</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Services</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{services?.length || 0}</div>
        </div>
        {systemInfo && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Total RAM</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{systemInfo.totalRAMGB} GB</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">CPU Cores</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{systemInfo.cpuCores}</div>
            </div>
          </>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Last Update</div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}
          </div>
        </div>
      </div>

      {cycleTls && !cycleTls.error && cycleTls.cap != null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-emerald-500">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">CycleTLS (this poll)</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            in-use <span className="font-mono text-gray-900 dark:text-white">{cycleTls.inUse}</span> / cap{' '}
            <span className="font-mono text-gray-900 dark:text-white">{cycleTls.cap}</span>
            {cycleTls.multiProcess ? ' · multi-process aggregate' : ''}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Processes</option>
              <option value="relevant">Relevant (Chrome/Node)</option>
              <option value="chrome">Chrome/Chromium</option>
              <option value="node">Node.js</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="cpu">CPU %</option>
              <option value="mem">Memory %</option>
              <option value="pid">PID</option>
            </select>
          </div>
        </div>
      </div>

      {/* Services Grouped View */}
      <div className="space-y-4">
        {(!services || services.length === 0) ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <div className="text-gray-500 dark:text-gray-400">No processes found</div>
          </div>
        ) : (
          services.map((service) => {
            const isExpanded = expandedServices.has(service.serviceType);
            const toggleService = () => {
              const newExpanded = new Set(expandedServices);
              if (isExpanded) {
                newExpanded.delete(service.serviceType);
              } else {
                newExpanded.add(service.serviceType);
              }
              setExpandedServices(newExpanded);
            };

            // Sort processes within service
            const sortedProcesses = [...service.processes].sort((a, b) => {
              if (sortBy === 'cpu') {
                return parseFloat(b.cpu) - parseFloat(a.cpu);
              } else if (sortBy === 'mem') {
                return parseFloat(b.mem) - parseFloat(a.mem);
              } else {
                return b.pid - a.pid;
              }
            });

            return (
              <div key={service.serviceType} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {/* Service Header - Clickable to expand/collapse */}
                <button
                  onClick={toggleService}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                      {service.serviceType === 'other' ? 'Other Processes' : `Service: ${service.serviceType}`}
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">{service.processCount} processes</span>
                      <span className={`font-medium ${
                        parseFloat(service.totalCpu) > 50 ? 'text-red-600 dark:text-red-400' :
                        parseFloat(service.totalCpu) > 20 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        Total CPU: {service.totalCpu}%
                      </span>
                      <span className={`font-medium ${
                        parseFloat(service.totalMem) > 10 ? 'text-red-600 dark:text-red-400' :
                        parseFloat(service.totalMem) > 5 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        Total Memory: {service.totalMem}% ({service.totalMemMB.toFixed(1)} MB)
                      </span>
                    </div>
                  </div>
                  <div className="text-gray-400 dark:text-gray-500">
                    {isExpanded ? '▼' : '▶'}
                  </div>
                </button>

                {/* Service Processes - Collapsible */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              PID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              CPU %
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Memory %
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Memory (MB)
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Command
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedProcesses.map((process) => (
                            <tr
                              key={process.pid}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                process.isRelevant ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                              }`}
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                                {process.pid}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                <span className={`font-medium ${
                                  parseFloat(process.cpu) > 50 ? 'text-red-600 dark:text-red-400' :
                                  parseFloat(process.cpu) > 20 ? 'text-yellow-600 dark:text-yellow-400' :
                                  'text-gray-600 dark:text-gray-400'
                                }`}>
                                  {process.cpu}%
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                <span className={`font-medium ${
                                  parseFloat(process.mem) > 10 ? 'text-red-600 dark:text-red-400' :
                                  parseFloat(process.mem) > 5 ? 'text-yellow-600 dark:text-yellow-400' :
                                  'text-gray-600 dark:text-gray-400'
                                }`}>
                                  {process.mem}%
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                <span className="font-mono">
                                  {process.memMB ? process.memMB.toFixed(1) : '0.0'} MB
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                <div className="max-w-2xl truncate" title={process.command}>
                                  {process.command}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    rateLimiter: {
      minDelayBetweenRequests: 2000,
      maxDelayBetweenRequests: 5000
    },
    automationQueues: {
      inboxScanRateLimit: 500,
      dmReplyRateLimit: 200,
      fullScanRateLimit: 50,
      failedDMRetryRateLimit: 50,
      followBackScanRateLimit: 10,
      followBackDMRateLimit: 50,
      followBackUnfollowRateLimit: 20,
      followBackRetryRateLimit: 5
    },
    automationWorkers: {
      inboxScanConcurrency: 500,
      dmReplyConcurrency: 200,
      fullScanConcurrency: 50,
      failedDMRetryConcurrency: 50
    },
    automationDelays: {
      inboxScanDelayMin: 45,
      inboxScanDelayMax: 60,
      fullScanDelayMin: 300,
      fullScanDelayMax: 600
    },
    massTaskWorkers: {
      massDMConcurrency: 50,
      massLikeConcurrency: 50,
      massFollowConcurrency: 50,
      followBackScanConcurrency: 50,
      followBackDMConcurrency: 200,
      followBackUnfollowConcurrency: 100,
      followBackRetryConcurrency: 50
    },
    dmPollingMethod: 'inbox_initial_state',
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/system-settings');
      if (response.data.success) {
        const fetchedSettings = response.data.settings;
        setFormData(prev => ({
          rateLimiter: { ...prev.rateLimiter, ...(fetchedSettings.rateLimiter || {}) },
          automationQueues: { ...prev.automationQueues, ...(fetchedSettings.automationQueues || {}) },
          automationWorkers: { ...prev.automationWorkers, ...(fetchedSettings.automationWorkers || {}) },
          automationDelays: { ...prev.automationDelays, ...(fetchedSettings.automationDelays || {}) },
          massTaskWorkers: { ...prev.massTaskWorkers, ...(fetchedSettings.massTaskWorkers || {}) },
          dmPollingMethod: fetchedSettings.dmPollingMethod || prev.dmPollingMethod || 'inbox_initial_state',
        }));
      }
    } catch (error) {
      toast.error('Failed to fetch system settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put('/admin/system-settings', {
        settings: formData
      });
      if (response.data.success) {
        toast.success('System settings updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update system settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseInt(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">System Settings</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure automation queue settings, rate limits, and worker concurrency
        </p>
      </div>

      <div className="space-y-8">
        {/* DM Polling Method */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">DM Inbox Polling Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Polling method
              </label>
              <select
                value={formData.dmPollingMethod || 'inbox_initial_state'}
                onChange={(e) => setFormData(prev => ({ ...prev, dmPollingMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="inbox_initial_state">inbox_initial_state (current – includes 5 min full inbox scan)</option>
                <option value="getInitialXChatPage">getInitialXChatPage (new GraphQL, non-E2EE)</option>
                <option value="xchat">XCHAT (full E2EE – key unwrap + NaCl decrypt + WebSocket)</option>
              </select>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose how DMs are polled: current API, GraphQL, or full XCHAT E2EE with NaCl decryption.</p>
            </div>
          </div>
        </div>

        {/* Rate Limiter Settings */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Global Per-Account Rate Limiter</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Min Delay Between Requests (ms)
              </label>
              <input
                type="number"
                value={formData.rateLimiter.minDelayBetweenRequests}
                onChange={(e) => handleChange('rateLimiter', 'minDelayBetweenRequests', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1000"
                max="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Max Delay Between Requests (ms)
              </label>
              <input
                type="number"
                value={formData.rateLimiter.maxDelayBetweenRequests}
                onChange={(e) => handleChange('rateLimiter', 'maxDelayBetweenRequests', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="2000"
                max="30000"
              />
            </div>
          </div>
        </div>

        {/* Automation Queue Rate Limits */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automation Queue Rate Limits (Global)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inbox Scan Rate Limit (scans/second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.inboxScanRateLimit}
                onChange={(e) => handleChange('automationQueues', 'inboxScanRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                DM Reply Rate Limit (replies/second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.dmReplyRateLimit}
                onChange={(e) => handleChange('automationQueues', 'dmReplyRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Scan Rate Limit (scans/second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.fullScanRateLimit}
                onChange={(e) => handleChange('automationQueues', 'fullScanRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Failed DM Retry Rate Limit (retries/second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.failedDMRetryRateLimit}
                onChange={(e) => handleChange('automationQueues', 'failedDMRetryRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
          </div>
        </div>

        {/* Follow-back queue rate limits */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Follow-back queue rate limits (global)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back scan (per second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.followBackScanRateLimit}
                onChange={(e) => handleChange('automationQueues', 'followBackScanRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back DM (per second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.followBackDMRateLimit}
                onChange={(e) => handleChange('automationQueues', 'followBackDMRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back unfollow (per second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.followBackUnfollowRateLimit}
                onChange={(e) => handleChange('automationQueues', 'followBackUnfollowRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back retry (per second)
              </label>
              <input
                type="number"
                value={formData.automationQueues.followBackRetryRateLimit}
                onChange={(e) => handleChange('automationQueues', 'followBackRetryRateLimit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
          </div>
        </div>

        {/* Automation Worker Concurrency */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automation Worker Concurrency (Per Instance)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inbox Scan Concurrency
              </label>
              <input
                type="number"
                value={formData.automationWorkers.inboxScanConcurrency}
                onChange={(e) => handleChange('automationWorkers', 'inboxScanConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                DM Reply Concurrency
              </label>
              <input
                type="number"
                value={formData.automationWorkers.dmReplyConcurrency}
                onChange={(e) => handleChange('automationWorkers', 'dmReplyConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Scan Concurrency
              </label>
              <input
                type="number"
                value={formData.automationWorkers.fullScanConcurrency}
                onChange={(e) => handleChange('automationWorkers', 'fullScanConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Failed DM Retry Concurrency
              </label>
              <input
                type="number"
                value={formData.automationWorkers.failedDMRetryConcurrency}
                onChange={(e) => handleChange('automationWorkers', 'failedDMRetryConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
          </div>
        </div>

        {/* Automation Delay Ranges */}
        <div className="pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automation Delay Ranges (Randomized Per Account)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inbox Scan Delay Min (seconds)
              </label>
              <input
                type="number"
                value={formData.automationDelays.inboxScanDelayMin}
                onChange={(e) => handleChange('automationDelays', 'inboxScanDelayMin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="10"
                max="300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inbox Scan Delay Max (seconds)
              </label>
              <input
                type="number"
                value={formData.automationDelays.inboxScanDelayMax}
                onChange={(e) => handleChange('automationDelays', 'inboxScanDelayMax', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="20"
                max="600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Scan Delay Min (seconds)
              </label>
              <input
                type="number"
                value={formData.automationDelays.fullScanDelayMin}
                onChange={(e) => handleChange('automationDelays', 'fullScanDelayMin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="60"
                max="1800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Scan Delay Max (seconds)
              </label>
              <input
                type="number"
                value={formData.automationDelays.fullScanDelayMax}
                onChange={(e) => handleChange('automationDelays', 'fullScanDelayMax', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="120"
                max="3600"
              />
            </div>
          </div>
        </div>

        {/* Mass Task Worker Concurrency */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Mass Task Worker Concurrency (Per Instance)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mass DM Concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.massDMConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'massDMConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mass Like Concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.massLikeConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'massLikeConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mass Follow Concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.massFollowConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'massFollowConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back scan concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.followBackScanConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'followBackScanConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back DM concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.followBackDMConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'followBackDMConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back unfollow concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.followBackUnfollowConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'followBackUnfollowConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-back retry concurrency
              </label>
              <input
                type="number"
                value={formData.massTaskWorkers.followBackRetryConcurrency}
                onChange={(e) => handleChange('massTaskWorkers', 'followBackRetryConcurrency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="1"
                max="1000"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;

